import { assistantClient, pageParams, type Page } from './assistant-client';
import { withApiErrors } from './http-error';
import type {
  ConversationSummary,
  ChatMessage,
  Role,
} from '../types/assistant.types';

/** Wire shape of assistant_messages: uppercase role, `text` not `content`. */
interface MessageRow {
  id: number;
  client_message_id?: string;
  role: 'USER' | 'ASSISTANT';
  text: string;
  blocks?: unknown[];
  attachments?: { name: string; pages: number }[];
  created_at: string;
}

export async function listConversations(
  page = 1,
  search = ''
): Promise<Page<ConversationSummary>> {
  return (
    await assistantClient.get<Page<ConversationSummary>>(
      'assistant/conversations/',
      { params: { ...pageParams(page), ...(search ? { search } : {}) } }
    )
  ).data;
}

/**
 * One page of a transcript, NEWEST FIRST — the server pages backwards from the
 * latest message (10 per page), so page 1 is the tail of the conversation.
 * Callers reverse the accumulated pages to render oldest-at-top.
 */
export async function getMessages(
  id: number,
  page = 1
): Promise<Page<ChatMessage>> {
  const { data } = await assistantClient.get<Page<MessageRow>>(
    `assistant/conversations/${id}/messages/`,
    { params: pageParams(page) }
  );
  return {
    ...data,
    results: data.results.map((m) => ({
      id: m.client_message_id || String(m.id),
      role: m.role.toLowerCase() as Role,
      content: m.text,
      blocks: Array.isArray(m.blocks) ? m.blocks : [],
      attachments: m.attachments ?? [],
      created_at: m.created_at,
    })),
  };
}

// The three writes below go through withApiErrors like every other service in
// this feature: their callers render `error.message`, and an unwrapped axios
// rejection only ever says "Request failed with status code 400" — never the
// reason the server gave for refusing.

export async function renameConversation(
  id: number,
  title: string
): Promise<void> {
  await withApiErrors(
    assistantClient.patch(`assistant/conversations/${id}/`, { title })
  );
}

/** Closes the thread server-side; the transcript and audit trail survive. */
export async function deleteConversation(id: number): Promise<void> {
  await withApiErrors(
    assistantClient.post(`assistant/conversations/${id}/erase/`, {
      confirm_conversation_id: id,
    })
  );
}

/** A pending action's id IS its confirmation token. */
export async function cancelPendingAction(pendingId: number): Promise<void> {
  // org slug added by the client interceptor
  await withApiErrors(
    assistantClient.post(`assistant/pending-actions/${pendingId}/cancel/`)
  );
}
